import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useToast } from "@/features/toast";
import { Link, useNavigate } from "react-router-dom";
import { Mail, ArrowLeft } from "lucide-react";
import { apiClient } from "@/api/config/api-client";
import { API_ENDPOINTS } from "@/api/config/api-endpoints";
import { AuthStage } from "@/components/auth/auth-stage";
import {
  authFieldClass,
  authFooterClass,
  authFormClass,
  authLabelClass,
  authLinkClass,
  authPrimaryButtonClass,
  authStackClass,
  authTitleClass,
} from "@/components/auth/auth-ui";
import { AuthSupportLink } from "@/components/auth/auth-support-link";
import { AuthIconBadge } from "@/components/auth/auth-icon-badge";

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const { success, error } = useToast();
  const navigate = useNavigate();

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      error(t("auth.enterValidEmail"), {
        title: t("auth.forgotPasswordError"),
      });
      return;
    }

    setIsLoading(true);
    try {
      await apiClient.post(API_ENDPOINTS.forgotPassword, {
        email,
      });

      success(t("auth.checkYourEmailForReset"), {
        title: t("auth.resetLinkSent"),
      });

      // Email gönderildi sayfasına yönlendir
      navigate("/password-reset-sent", { state: { email } });
    } catch (errorResponse: any) {
      error(
        errorResponse.response?.data?.message || t("error.somethingWentWrong"),
        {
          title: t("auth.forgotPasswordError"),
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthStage>
          <div className={authStackClass}>
            {/* Header */}
            <div className="flex flex-col items-center gap-4">
              <AuthIconBadge>
                <Mail className="h-6 w-6 text-text-primary dark:text-text-primary" />
              </AuthIconBadge>
              <div className="text-center space-y-2">
                <h1 className={authTitleClass}>
                  {t("auth.forgotPassword")}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t("auth.forgotPasswordDescription")}
                </p>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleForgotPassword} className={authFormClass}>
              <div className="space-y-1">
                <label
                  htmlFor="email"
                  className={authLabelClass}
                >
                  {t("auth.email")}
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder={t("auth.enterEmail")}
                    className={`${authFieldClass} pl-9`}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                variant="darkPrimary"
                className={authPrimaryButtonClass}
                disabled={isLoading || !email}
              >
                {isLoading ? t("common.loading") : t("auth.sendResetLink")}
              </Button>
            </form>

            {/* Back to Login */}
            <div className="flex justify-center">
              <Link
                to="/login"
                className={`${authLinkClass} text-sm items-center gap-2`}
              >
                <ArrowLeft className="h-4 w-4" />
                {t("auth.backToLogin")}
              </Link>
            </div>

            {/* Destek çıkışı + Language/Theme Switcher */}
            <div className={authFooterClass}>
              <AuthSupportLink />
            </div>
          </div>
    </AuthStage>
  );
}
